import { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Box, Button, Chip, Stack, Typography } from "@mui/material";
import VideocamOffIcon from "@mui/icons-material/VideocamOff";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { usePrivateMediaPlaybackUrl } from "../../hooks/useEventInstance";

interface Video {
	url?: string;
	durationSeconds?: number;
}

interface Props {
	video?: Video;
	mode?: "view" | "review";
	/** Required for a captured PHI video: the signed link is fetched per submission. */
	enrollmentId?: string;
	instanceId?: string;
	/** The requesting reviewer. SIMS-8306 checks entitlement against this and audit-logs it. */
	reviewerId?: string;
	/** Whether this submission actually holds a private captured video. */
	hasPrivateMedia?: boolean;
	/**
	 * SIMS-8319 — fires the first time playback starts.
	 *
	 * 🔴 Screen `3f`: "Approve and Reject stay disabled until the video has been played. A decision
	 * cannot be recorded on media the reviewer has not seen." No acceptance criterion carries this;
	 * the frame states it outright, and it is the control that stops a $60 payment — or a FINAL
	 * rejection — being recorded against a video nobody opened.
	 */
	onPlayed?: () => void;
}

/**
 * SIMS-9318: the video's height. It follows the viewport so the expiry chip, the notices, the
 * details and Approve/Reject below it stay on screen without scrolling (455px is what they and
 * the dialog chrome need), and it is bounded so a phone recording is never a sliver or a wall.
 * Exported so the tests pin the same value the component uses.
 */
export const VIDEO_HEIGHT_CSS = "clamp(200px, calc(100vh - 455px), 480px)";

/** Seconds -> "m:ss", matching the frame's "Secure link expires in 4:12". */
const formatCountdown = (totalSeconds: number): string => {
	const safe = Math.max(0, Math.floor(totalSeconds));
	return `${Math.floor(safe / 60)}:${(safe % 60).toString().padStart(2, "0")}`;
};

/**
 * Captured-video review player — SIMS-8319 AC2, screen `3f`.
 *
 * Replaces the SIMS-4988 stub ("Video review will land with mobile capture").
 *
 * ## The link is never exposed
 *
 * Screen `3f`: *"Playback uses a time-limited signed link. The link is never displayed, copyable,
 * or downloadable, and every view is audit-logged."* So: no anchor, `controlsList="nodownload"`,
 * context menu suppressed, and the URL is never rendered or logged. For its whole five-minute life
 * that string is a bearer token for PHI — anyone holding it can fetch the video with no further
 * authorisation.
 *
 * ## Expiry is a state, not an error
 *
 * A live countdown and a **Refresh link** while valid; a full **Request new link** state once it
 * lapses. Never a broken player. The TTL is read from the server's own response rather than
 * assumed, so the countdown cannot drift from the signature.
 */
export const VideoPlayer = ({
	video,
	mode,
	enrollmentId,
	instanceId,
	reviewerId,
	hasPrivateMedia,
	onPlayed,
}: Props) => {
	const playback = usePrivateMediaPlaybackUrl(
		enrollmentId,
		instanceId,
		reviewerId,
	);
	const [url, setUrl] = useState<string | undefined>(video?.url);
	const [secondsLeft, setSecondsLeft] = useState(0);
	const hasPlayedRef = useRef(false);

	const request = useCallback((): void => {
		if (!enrollmentId || !instanceId || !reviewerId) return;
		playback.mutate(undefined, {
			onSuccess: (grant) => {
				setUrl(grant?.url);
				// Trust the server's TTL rather than a local constant — if SIMS-8306's expiry
				// changes, the countdown follows it instead of lying.
				setSecondsLeft(grant?.expiresInSeconds ?? 0);
			},
			onError: () => {
				setUrl(undefined);
				setSecondsLeft(0);
			},
		});
	}, [enrollmentId, instanceId, reviewerId]);

	// Fetch once when the modal opens on a submission that has private media.
	useEffect(() => {
		if (hasPrivateMedia && !url) request();
	}, [hasPrivateMedia]);

	// The countdown. At zero the URL is dropped, so the <video> element can never hold a dead
	// source and show the browser's own broken-media error instead of the designed state.
	useEffect(() => {
		if (secondsLeft <= 0) return undefined;
		const id = setInterval(() => {
			setSecondsLeft((s) => {
				if (s <= 1) {
					setUrl(undefined);
					return 0;
				}
				return s - 1;
			});
		}, 1000);
		return () => clearInterval(id);
	}, [secondsLeft]);

	const handlePlay = useCallback((): void => {
		if (hasPlayedRef.current) return;
		hasPlayedRef.current = true;
		onPlayed?.();
	}, [onPlayed]);

	// No private media on this submission — the old stub's job, kept for WATCH_VIDEO, which
	// streams from the public CDN and has no review surface of its own.
	if (!hasPrivateMedia && !video?.url) {
		return (
			<Stack
				alignItems="center"
				spacing={1}
				sx={{
					p: 4,
					bgcolor: "action.hover",
					borderRadius: 1,
					color: "text.secondary",
				}}
				data-testid="video-player-empty"
			>
				<VideocamOffIcon fontSize="large" />
				<Typography variant="body2" align="center">
					No video was submitted for this activity.
				</Typography>
				<Box />
			</Stack>
		);
	}

	// Expired, or never issued. Screen `3f`'s second state.
	if (!url) {
		return (
			<Stack
				alignItems="center"
				spacing={1.5}
				sx={{
					p: 4,
					border: 1,
					borderColor: "error.main",
					borderRadius: 1,
					bgcolor: "action.hover",
				}}
				data-testid="video-player-expired"
			>
				<LinkOffIcon fontSize="large" color="error" />
				<Typography variant="subtitle1" fontWeight={700}>
					Playback link expired
				</Typography>
				<Typography variant="body2" align="center" color="text.secondary">
					Signed links last 5 minutes. Request a new one to keep reviewing.
				</Typography>
				<Button
					variant="contained"
					onClick={request}
					disabled={playback.isPending}
					data-testid="video-player-request-link"
				>
					REQUEST NEW LINK
				</Button>
			</Stack>
		);
	}

	return (
		<Stack spacing={1.5} data-testid="video-player">
			<video
				src={url}
				controls
				playsInline
				preload="metadata"
				// 🔴 The frame: the link is "never displayed, copyable, or downloadable".
				controlsList="nodownload noplaybackrate"
				disablePictureInPicture
				onContextMenu={(e) => e.preventDefault()}
				onPlay={handlePlay}
				// SIMS-9318: a fixed height with width auto, so the element takes the video's own
				// aspect ratio and sits centred. A full-width box letterboxed a 9:16 phone recording
				// into a black band with a small picture; maxWidth keeps a landscape video inside the
				// dialog. Fullscreen is the browser's and plays at full size.
				style={{
					display: "block",
					width: "auto",
					maxWidth: "100%",
					height: VIDEO_HEIGHT_CSS,
					margin: "0 auto",
					objectFit: "contain",
					borderRadius: 4,
					backgroundColor: "#000",
				}}
				data-testid="video-player-media"
			/>
			<Stack direction="row" justifyContent="space-between" alignItems="center">
				<Chip
					size="small"
					icon={<LockOutlinedIcon />}
					label={`Secure link expires in ${formatCountdown(secondsLeft)}`}
					data-testid="video-player-countdown"
				/>
				<Button
					size="small"
					onClick={request}
					disabled={playback.isPending}
					data-testid="video-player-refresh"
				>
					Refresh link
				</Button>
			</Stack>
			<Alert severity="success" icon={<LockOutlinedIcon fontSize="inherit" />}>
				<strong>Private media (PHI).</strong> Playback uses a time-limited
				signed link. The link is never displayed, copyable, or downloadable, and
				every view is audit-logged.
			</Alert>
			{mode === "review" ? (
				<Alert severity="info" data-testid="video-player-agent-notice">
					No agent ran on this submission — capture-video events are
					human-reviewed. Auto-validation is a future catalog entry.
				</Alert>
			) : null}
		</Stack>
	);
};
